import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Modal from 'LuxKit/Modal/Modal'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import config from 'constants/config'
import useTourStyledLabels from 'hooks/Tours/useTourStyledLabels'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import type { ComponentProps } from 'react'
import React, { useCallback } from 'react'
import { useIntl } from 'react-intl'

export type TourStyledSignatureSeriesProductTypeInfoModalResult = 'primary-action' | undefined

type InheritedModalProps = Pick<ComponentProps<typeof Modal>, 'primaryActionText' | 'primaryActionTo'>

type Props = InheritedModalProps

function TourStyledSignatureSeriesProductTypeInfoModal({
  primaryActionText,
  primaryActionTo,
}: Props) {
  const modalElement = useModalElementContext<TourStyledSignatureSeriesProductTypeInfoModalResult>()
  const intl = useIntl()
  const { styledProductTypeTitles } = useTourStyledLabels()

  const handlePrimaryClick = useCallback(() => {
    modalElement.resolve('primary-action')
  }, [modalElement])

  const title = <>{intl.formatMessage(
    {
      id: 'Ro_v6tXKltWR2iMPNzVY',
      defaultMessage: '{brandName} Tour Styles',
      description: 'Modal title for tour styles information with brand name',
    },
    { brandName: config.brandConfig.name },
  )}
  </>

  const descriptionText = intl.formatMessage(
    {
      id: 'qGjChcaH003whl5kOCbq',
      defaultMessage: 'These expertly curated journeys are co-designed by some of Australia’s most trusted personalities and tastemakers. With itineraries built on authentic recommendations, Luxury Escapes’ Insider Series takes you beyond the ordinary on an adventure designed by an expert.',
      description: 'Description of the Insider Series tour style explaining expert-curated luxury journeys',
    },
  )

  const insiderCuratedHeading = intl.formatMessage(
    {
      id: 'uh5K3YCs0x5BYQJQIHOj',
      defaultMessage: 'Insider-Curated Itinerary',
      description: 'Feature heading for Insider Series tours highlighting expert-curated itineraries',
    },
  )

  const insiderCuratedDescription = intl.formatMessage(
    {
      id: 'RYSl8ltwSFQXDHG0StlR',
      defaultMessage: 'Explore a destination through an expert’s eyes, with curated experiences & hidden gems they love most.',
      description: 'Feature description of insider-curated itineraries with hidden local gems',
    },
  )

  const premiumAccommodationHeading = intl.formatMessage(
    {
      id: 'zmPt7FgReL_1ySTVoHbI',
      defaultMessage: 'Premium Accommodation',
      description: 'Feature heading for Insider Series tours highlighting premium hotels',
    },
  )

  const premiumAccommodationDescription = intl.formatMessage(
    {
      id: 'GNWCF9Ci7C9TDY6aSnGJ',
      defaultMessage: 'Unwind in elevated hotels designed for enhanced comfort and quality.',
      description: 'Feature description of premium accommodation in Insider Series tours',
    },
  )

  const smallGroupHeading = intl.formatMessage(
    {
      id: 'v1iUouo_uJNF-382vN4Q',
      defaultMessage: 'Small-Group Journeys',
      description: 'Feature heading for Insider Series tours highlighting small group travel',
    },
  )

  const smallGroupDescription = intl.formatMessage(
    {
      id: 'IMU_h-N4olXR55Hlf3_P',
      defaultMessage: 'Travel in a small group for easier access to each destination.',
      description: 'Feature description of small-group travel benefit in Insider Series tours',
    },
  )

  return <Modal
    size="L"
    title={title}
    subtitle={styledProductTypeTitles.insider_series_tour}
    primaryActionText={primaryActionText}
    primaryActionTo={primaryActionTo}
    onPrimaryActionClick={!primaryActionTo ? handlePrimaryClick : undefined}
  >
    <VerticalSpacer gap={24}>
      <BodyText variant="medium" colour="neutral-two">
        {descriptionText}
      </BodyText>
      <Group
        direction="vertical"
        tabletDirection="horizontal"
        tabletHorizontalAlign="stretch"
        gap={24}
      >
        <VerticalSpacer gap={4}>
          <BodyText variant="medium" weight="bold" colour="neutral-one">
            {insiderCuratedHeading}
          </BodyText>
          <BodyText variant="medium" colour="neutral-two">
            {insiderCuratedDescription}
          </BodyText>
        </VerticalSpacer>
        <VerticalSpacer gap={4}>
          <BodyText variant="medium" weight="bold" colour="neutral-one">
            {premiumAccommodationHeading}
          </BodyText>
          <BodyText variant="medium" colour="neutral-two">
            {premiumAccommodationDescription}
          </BodyText>
        </VerticalSpacer>
        <VerticalSpacer gap={4}>
          <BodyText variant="medium" weight="bold" colour="neutral-one">
            {smallGroupHeading}
          </BodyText>
          <BodyText variant="medium" colour="neutral-two">
            {smallGroupDescription}
          </BodyText>
        </VerticalSpacer>
      </Group>
    </VerticalSpacer>
  </Modal>
}

export default TourStyledSignatureSeriesProductTypeInfoModal
